import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { outcomesResponseSchema, type Outcome, type Submission } from '@competa/shared';
import { apiRequest, apiUrl } from '../../api/client';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ImageViewer } from '../../components/ImageViewer';
import viewerStyles from '../../components/ImageViewer.module.css';
import { TextField } from '../../components/TextField';
import { errorMessage } from '../../i18n/errors';
import styles from './Competitions.module.css';
import { OUTCOME_KEYS, SUCCESS_LEVELS, toggleLevel } from './outcomes';

/** Zwischenstand der Eingabe je Einreichung. */
interface Draft {
  outcome: Outcome;
  note: string;
}

/** Eigenschaften des Reiters „Ergebnisse". */
interface ResultsTabProps {
  competitionId: number;
  closed: boolean;
  submissions: Submission[];
}

/**
 * Reiter „Ergebnisse": Je Bild wird per Kontrollkästchen Annahme, Urkunde/HM oder Medaille gesetzt; die
 * Stufen schließen sich aus. Gespeichert wird in einem Schritt, optional mit Entscheidungsdatum und „Alle
 * übrigen ablehnen". Aus Altdaten abgeleitete Ergebnisse sind markiert und lassen sich bestätigen.
 * @param props Wettbewerb und Einreichungen.
 */
export function ResultsTab({ competitionId, closed, submissions }: ResultsTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [rejectRemaining, setRejectRemaining] = useState(false);
  const [decidedAt, setDecidedAt] = useState('');
  const [saved, setSaved] = useState(false);
  const [viewIndex, setViewIndex] = useState<number | null>(null);

  /** Aktueller Eingabestand einer Einreichung (Entwurf oder gespeicherter Wert). */
  function draftOf(submission: Submission): Draft {
    return (
      drafts[submission.id] ?? { outcome: submission.outcome, note: submission.awardNote ?? '' }
    );
  }

  const changes = submissions.flatMap((submission) => {
    const draft = draftOf(submission);
    const same =
      draft.outcome === submission.outcome && draft.note === (submission.awardNote ?? '');
    return same
      ? []
      : [{ submissionId: submission.id, outcome: draft.outcome, awardNote: draft.note || null }];
  });
  const openCount = submissions.filter((s) => draftOf(s).outcome === 'open').length;

  const save = useMutation({
    mutationFn: () =>
      apiRequest('PATCH', `/competitions/${competitionId}/outcomes`, {
        body: {
          changes,
          rejectRemaining,
          ...(decidedAt ? { decidedAt } : {}),
        },
        schema: outcomesResponseSchema,
      }),
    onSuccess: async () => {
      setDrafts({});
      setRejectRemaining(false);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['competition', competitionId] });
      await queryClient.invalidateQueries({ queryKey: ['competitions'] });
    },
  });
  const confirm = useMutation({
    mutationFn: () => apiRequest('POST', `/competitions/${competitionId}/confirm`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['competition', competitionId] }),
  });

  /** Ändert den Entwurf einer Einreichung. */
  function update(submission: Submission, patch: Partial<Draft>): void {
    setSaved(false);
    setDrafts({ ...drafts, [submission.id]: { ...draftOf(submission), ...patch } });
  }

  const inferred = submissions.filter((submission) => submission.legacyInferred).length;
  const canSave = changes.length > 0 || (rejectRemaining && openCount > 0);

  if (submissions.length === 0) return <p>{t('competitions.noSubmissions')}</p>;
  return (
    <div className={styles.panel}>
      {closed && <p>{t('competitions.closedHint')}</p>}
      {inferred > 0 && (
        <div className={styles.warnings} role="status">
          <p>{t('competitions.inferred', { count: inferred })}</p>
          <Button variant="secondary" disabled={confirm.isPending} onClick={() => confirm.mutate()}>
            {t('competitions.confirmInferred')}
          </Button>
        </div>
      )}
      <table className={styles.table}>
        <thead>
          <tr>
            <th>{t('images.preview')}</th>
            <th>{t('images.titleLabel')}</th>
            <th>{t('competitions.result')}</th>
            <th>{t('competitions.award')}</th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((submission) => {
            const draft = draftOf(submission);
            const effective = closed ? submission.effectiveOutcome : draft.outcome;
            return (
              <tr key={submission.id}>
                <td>
                  {submission.hasThumb && (
                    <button
                      type="button"
                      className={viewerStyles.thumbButton}
                      aria-label={t('viewer.open', { name: submission.imageTitle })}
                      onClick={() => setViewIndex(submissions.indexOf(submission))}
                    >
                      <img
                        className={styles.thumb}
                        src={apiUrl(`/images/${submission.imageId}/thumb`)}
                        alt=""
                        loading="lazy"
                      />
                    </button>
                  )}
                </td>
                <td>
                  {submission.imageTitle}
                  {submission.category && <div className={styles.meta}>{submission.category}</div>}
                  {submission.legacyInferred && (
                    <Badge tone="warning">{t('competitions.inferredBadge')}</Badge>
                  )}
                </td>
                <td>
                  {SUCCESS_LEVELS.map((level) => (
                    <label key={level} className={styles.check}>
                      <input
                        type="checkbox"
                        disabled={closed}
                        checked={effective === level}
                        aria-label={`${submission.imageTitle}: ${t(`competitions.outcome.${OUTCOME_KEYS[level]}`)}`}
                        onChange={() =>
                          update(submission, { outcome: toggleLevel(draft.outcome, level) })
                        }
                      />
                      {t(`competitions.outcome.${OUTCOME_KEYS[level]}`)}
                    </label>
                  ))}
                  {effective === 'rejected' && (
                    <Badge tone="danger">{t('competitions.outcome.rejected')}</Badge>
                  )}
                </td>
                <td>
                  {(effective === 'honorable_mention' || effective === 'medal') && (
                    <input
                      className={styles.noteInput}
                      disabled={closed}
                      aria-label={t('competitions.awardOf', { name: submission.imageTitle })}
                      placeholder={t('competitions.awardPlaceholder')}
                      value={draft.note}
                      onChange={(event) => update(submission, { note: event.target.value })}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!closed && (
        <div className={styles.savebar}>
          <TextField
            label={t('competitions.decidedAt')}
            hint={t('competitions.decidedAtHint')}
            type="date"
            value={decidedAt}
            onChange={(event) => setDecidedAt(event.target.value)}
          />
          <label className={styles.check}>
            <input
              type="checkbox"
              checked={rejectRemaining}
              onChange={(event) => setRejectRemaining(event.target.checked)}
            />
            {t('competitions.rejectRemaining', { count: openCount })}
          </label>
          <Button disabled={!canSave || save.isPending} onClick={() => save.mutate()}>
            {t('competitions.saveResults')}
          </Button>
          {saved && (
            <span role="status" className={styles.success}>
              {t('competitions.saved')}
            </span>
          )}
        </div>
      )}
      {(save.error || confirm.error) && (
        <p role="alert" className={styles.error}>
          {errorMessage(t, save.error ?? confirm.error)}
        </p>
      )}
      <ImageViewer
        items={submissions.map((s) => ({ id: s.imageId, title: s.imageTitle }))}
        index={viewIndex}
        onIndexChange={setViewIndex}
        onClose={() => setViewIndex(null)}
      />
    </div>
  );
}
